import { connect } from 'react-redux';
import { Button, Card, Form, Input, message, Modal, Row } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createRawAddress, emailValidator, nullSafeGet } from '../utils/DataAccessUtils';
import PhoneNumberComponentAntdV4 from './PhoneNumberComponent/PhoneNumberComponentAntdV4';
import { addressValidatorFormv4 } from '../utils/LocationUtils';
import { LocationAutocomplete } from './location_autocomplete/LocationAutocomplete';
import { ROLE_TYPES } from '../utils/DataConstants';
import {
	contractorClientsRestCrudThunksForBuyer,
	contractorClientsRestCrudThunksForSupplier,
} from '../thunks/contractor_clients_thunks';
import { usersRestCrudThunksForSupplier } from '../thunks/users_thunks';

const FORM_NAME = 'contractor-client-contract-details-form';

const EditContractorClientContactDetails = ({
	update,
	targetCollectionName,
	formData,
	visible,
	close,
}) => {
	const [form] = Form.useForm();
	const [loading, setLoading] = useState(false);

	const isUpdate = useMemo(() => nullSafeGet('id', formData), [formData]);
	const handleSubmit = useCallback(
		(values) => {
			const entity = {
				...(formData || {}),
				...values,
				isSupplier: true,
				isBuyer: false,
			};

			setLoading(true);
			update(entity, 'id', targetCollectionName)
				.then(() => {
					message.success(`Client contact details updated successfully!`);
					close();
				})
				.catch((err) => message.error(err))
				.finally(() => {
					setLoading(false);
				});
		},
		[formData, isUpdate, update, targetCollectionName]
	);

	const initialValues = useMemo(
		() => ({
			...(formData || {}),
			isSupplier: true,
			isBuyer: false,
		}),
		[formData]
	);

	const handleLocationAutocompleteSelect = useCallback(
		(addr, lonLat, parsedAddress) => {
			form.setFieldsValue({
				addressInput: parsedAddress,
				'primaryAddress-streetAddress1': parsedAddress.streetAddress1,
				'primaryAddress-streetAddress2Opt': parsedAddress.streetAddress2Opt,
				'primaryAddress-city': parsedAddress.city,
				'primaryAddress-region': parsedAddress.region,
				'primaryAddress-postcode': parsedAddress.postcode,
				'primaryAddress-country': parsedAddress.country,
				longitude: lonLat.lng,
				latitude: lonLat.lat,
			});
			form.validateFields(['addressInput']);
		},
		[form]
	);
	const formItemStyle = { width: '100%' };

	return (
		<Modal
			visible={visible}
			onCancel={close}
			title={'Edit Contractor Client Details'}
			onOk={form.submit}
			okText={'Update Details'}
		>
			<Form
				id={FORM_NAME}
				layout="vertical"
				requiredMark={false}
				initialValues={initialValues}
				onFinish={handleSubmit}
				form={form}
			>
				<Form.Item
					label="Primary Contact Email Address"
					name={'primaryContactEmail'}
					style={formItemStyle}
					rules={[
						{ required: false, message: "Please input your primary contact's email!" },
						{ validator: emailValidator },
					]}
				>
					<Input placeholder="John.Doe@doeplumbing.com" />
				</Form.Item>
				<PhoneNumberComponentAntdV4
					form={form}
					style={formItemStyle}
					fieldProps={{
						initialValue: nullSafeGet('phoneNumber', formData),
						name: 'phoneNumber',
						label: 'Phone Number',
					}}
				/>
				<Form.Item
					required={true}
					label="Address"
					rules={[
						({ getFieldValue }) => ({
							validator: addressValidatorFormv4(getFieldValue),
						}),
					]}
					name="primaryAddress"
					style={formItemStyle}
				>
					<LocationAutocomplete
						initialValue={createRawAddress(nullSafeGet('address', formData))}
						placeholder={createRawAddress(nullSafeGet('address', formData))}
						onSelect={handleLocationAutocompleteSelect}
					/>
				</Form.Item>
				<Form.Item name="address-streetAddress1" hidden />
				<Form.Item name="address-streetAddress2Opt" hidden />
				<Form.Item name="address-city" hidden />
				<Form.Item name="address-region" hidden />
				<Form.Item name="address-postcode" hidden />
				<Form.Item name="address-country" hidden />
				<Form.Item name="longitude" hidden />
				<Form.Item name="latitude" hidden />
				<Form.Item required={true} label="Website" name="website" style={formItemStyle}>
					<Input />
				</Form.Item>
			</Form>
		</Modal>
	);
};

const mapStateToProps = (state) => ({
	users: state.users,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	update: (entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? contractorClientsRestCrudThunksForSupplier.update(entity)
				: contractorClientsRestCrudThunksForBuyer.update(entity)
		),
	fetchUsers: (params, targetCollectionName, pagination, sorting, filters, addToTargetCollection) =>
		dispatch(
			usersRestCrudThunksForSupplier.read(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	fetchMultipleUsers: (ids, targetCollectionName) =>
		dispatch(usersRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)),
});

const ComponentWithoutUserType = connect(
	mapStateToProps,
	mapDispatchToProps
)(EditContractorClientContactDetails);

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
	}),
	() => ({})
)(ComponentWithoutUserType);
